import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Col, Divider, Row, Space, Typography } from 'antd';
import { ReactComponent as IconBack } from 'assets/images/icons/iconBackVector.svg';
import LogoNormal from 'assets/images/gt-logo-menu-normal.svg';
import { ConnectEntity } from 'components/ConnectEntity';
import { FeatureDotBadge } from 'components/Custom/FeatureDotBadge';
import { RoundAutoComplete } from 'components/Custom/inputs';
import { StyledTooltip } from 'components/Custom/Tooltip';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { localizeDigitsForLocale } from 'i18n/arabicDigits';
import { formatDisplayDateRange } from 'i18n/calendarLabels';
import { localizeReturnMappingLabel } from 'i18n/returnTypeLabel';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import routes from 'routes';
import { turnDrawerOn } from 'store/app/actions';
import { DrawerType, LicenseType, MenuSize, Pages } from 'store/app/enums';
import { getCurrentPage, getMappingData } from 'store/app/selectors';
import { User } from 'store/auth/models';
import { getAuthUser, getAuthUserLicence } from 'store/auth/selectors';
import { Organisation } from 'store/organisation/models';
import {
  getOrgMappedReturns,
  getOrgOrganisations,
} from 'store/organisation/selectors';
import { getReturnData } from 'store/returns/selectors';
import { getUserMembers } from 'store/user/selectors';
import { getViesContacts } from 'store/vat-checker/selectors';
import { getVatRunData } from 'store/vat/selectors';
import styled from 'styled-components';
import { clearOrganisationDetailsTabStorage } from 'utils/clear-organisation-tab-storage';
import { lockLeaveEntityNavigation } from 'utils/leave-entity-navigation-lock';
import { markSkipPwaHomeRestore } from 'utils/pwa-last-entity';
import { buildPwaEntityTaxReturnsNavigateUrl } from 'utils/pwa-nav';
import { useSyncPwaHeaderHeight } from 'utils/useSyncPwaHeaderHeight';
import { parseGroupReturnContextFromSearch } from 'utils/group-return-context';
import { RenderOrganizationDetail } from 'utils/render-organization-details';
import { RenderUserDetail } from 'utils/render-user-details';
import { translatePageName } from 'utils/translate-page-name';

const SearchWrapper = styled.div`
  position: relative;
  display: flex;

  .ant-select-selection-search-input,
  .ant-select-selection-placeholder {
    padding-inline-start: 20px !important;
  }
`;

const StyledSearchOutlined = styled(SearchOutlined)`
  position: absolute;
  inset-inline-start: 12px;
  top: 14px;
  z-index: 1;
  color: var(--color-text-muted, #5a6a6a);
`;

const DropdownSection = styled.div`
  padding: 8px 12px;
`;

const OptionWrapper = styled.div`
  cursor: pointer;
  padding: 4px 0;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--color-surface, #f5f5f5);
  }
`;

const SectionTitle = styled.div`
  font-size: 12px;
  color: var(--color-text-muted, #5a6a6a);
  margin-bottom: 8px;
  font-weight: 600;
`;

/** Path: /vat/report or /vat/tifdReport /connection/:connectionId/mapping/:mappingId/log/:dataLogId */
function parseVatReportRoute(pathname: string): {
  connectionId?: string;
  mappingId?: string;
} {
  const markers = [
    `${routes.vat.report}/connection/`,
    `${routes.vat.tifdReport}/connection/`,
  ];
  for (const marker of markers) {
    const idx = pathname.indexOf(marker);
    if (idx === -1) continue;
    const rest = pathname.slice(idx + marker.length).split('/');
    if (rest.length >= 3 && rest[1] === 'mapping') {
      return { connectionId: rest[0], mappingId: rest[2] };
    }
  }
  return {};
}

interface PageTitle {
  main: string;
  pre: React.ReactNode | null;
}

interface HeaderProps {
  menuSize: MenuSize;
  pwaMobileNav?: boolean;
}

interface HeaderResolution {
  pageTitle: PageTitle;
  backLink?: string;
}

export const Header: React.FC<HeaderProps> = ({ menuSize, pwaMobileNav = false }) => {
  const loggedUser = useSelector(getAuthUser);
  const currentPage = useSelector(getCurrentPage);
  const mappedReturns = useSelector(getOrgMappedReturns);
  const mappingData = useSelector(getMappingData);
  const returnData = useSelector(getReturnData);
  const vatRunData = useSelector(getVatRunData);
  const members = useSelector(getUserMembers);
  const orgs = useSelector(getOrgOrganisations);
  const contacts = useSelector(getViesContacts);
  const userLicence = useSelector(getAuthUserLicence);
  const { t, i18n } = useTranslation();
  const { countries } = useCountryLabel();

  //const organisations = useSelector(getOrgMappedOrganisations);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [backLink, setBackLink] = React.useState(routes.main);
  const [showLine, setShowLine] = React.useState(false);
  const [pageTitle, setPageTitle] = React.useState<PageTitle | null>({
    main: translatePageName(t, currentPage.name),
    pre: null,
  });

  const [searchOptions, setSearchOptions] = React.useState<{
    entities: {
      value: string;
      label: React.ReactNode;
      organisation: Organisation;
    }[];
    users: { value: string; label: React.ReactNode; member: User }[];
  }>({ entities: [], users: [] });
  const [searchValue, setSearchValue] = React.useState<string>('');
  const searchRef = React.useRef<HTMLDivElement | null>(null);

  const handleSearch = React.useCallback(
    (value: string) => {
      if (!value || value.length < 2) {
        setSearchOptions({ entities: [], users: [] });
        return;
      }

      const searchLower = value.toLowerCase();

      const entities =
        orgs
          ?.filter(
            (org) =>
              org.friendlyName?.toLowerCase().includes(searchLower) ||
              org.legalName?.toLowerCase().includes(searchLower)
          )
          .map((org) => ({
            value: `entity-${org.uuid}`,
            label: (
              <RenderOrganizationDetail record={org} showOption noPadding />
            ),
            organisation: org,
          })) || [];

      const users =
        members
          ?.filter(
            (member) =>
              member.firstName.toLowerCase().includes(searchLower) ||
              member.lastName.toLowerCase().includes(searchLower) ||
              member.email?.toLowerCase().includes(searchLower)
          )
          .map((member) => ({
            value: `user-${member.accountUuid}`,
            label: <RenderUserDetail record={member} showOption noPadding />,
            member: member,
          })) || [];

      setSearchOptions({ entities, users });
    },
    [orgs, members]
  );

  const clearSearch = React.useCallback(() => {
    setSearchValue('');
    setSearchOptions({ entities: [], users: [] });
  }, []);

  const onSelect = React.useCallback(
    (value: string) => {
      if (value.startsWith('entity-')) {
        const orgId = value.replace('entity-', '');
        navigate(routes.organisations.organisation.replace(':id', orgId));
      } else if (value.startsWith('user-')) {
        const accountId = value.replace('user-', '');
        navigate(routes.account.details.replace(':accountId', accountId));
      }
      clearSearch();
    },
    [navigate, clearSearch]
  );

  const handleChange = React.useCallback(
    (value: string) => {
      setSearchValue(value);
      handleSearch(value);
    },
    [handleSearch]
  );

  // Clear search when clicking outside
  React.useEffect(() => {
    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;

      const clickedInsideSearch = searchRef.current?.contains(target);
      const dropdownEl = document.querySelector('.ant-select-dropdown');
      const clickedInsideDropdown = dropdownEl?.contains(target);

      if (!clickedInsideSearch && !clickedInsideDropdown) {
        clearSearch();
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () =>
      document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, [clearSearch]);

  const renderDropdown = React.useCallback(
    (menu: React.ReactElement) => {
      const hasEntities = searchOptions.entities.length > 0;
      const hasUsers = searchOptions.users.length > 0;

      if (!hasEntities && !hasUsers) return menu;

      return (
        <div>
          {hasEntities && (
            <DropdownSection>
              <SectionTitle>{t('organisation.entities')}</SectionTitle>
              {searchOptions.entities.map((option) => (
                <OptionWrapper
                  key={option.value}
                  onClick={() => onSelect(option.value)}
                >
                  {option.label}
                </OptionWrapper>
              ))}
            </DropdownSection>
          )}
          {hasEntities && hasUsers && <Divider style={{ margin: '8px 0' }} />}
          {hasUsers && (
            <DropdownSection>
              <SectionTitle>{t('main.users')}</SectionTitle>
              {searchOptions.users.map((option) => (
                <OptionWrapper
                  key={option.value}
                  onClick={() => onSelect(option.value)}
                >
                  {option.label}
                </OptionWrapper>
              ))}
            </DropdownSection>
          )}
        </div>
      );
    },
    [searchOptions, onSelect, t]
  );

  const allOptions = React.useMemo(
    () => [...searchOptions.entities, ...searchOptions.users],
    [searchOptions]
  );

  const onAddEntity = React.useCallback(() => {
    dispatch(
      turnDrawerOn({
        title: t('organisation.add-new-entity'),
        subtitle: t('organisation.connect-entity-subtitle'),
        type: DrawerType.CONNECT_ENTITY,
        component: <ConnectEntity />,
      })
    );
  }, [dispatch, t]);

  // Helper to create org breadcrumb link
  const createOrgBreadcrumb = React.useCallback(
    (orgName?: string, orgId?: string) => {
      if (!orgId) {
        return (
          <span style={{ color: 'inherit' }}>
            {orgName || t('navigation.organisation')}
          </span>
        );
      }
      return (
        <Link
          to={routes.organisations.organisation.replace(':id', orgId)}
          style={{ color: 'inherit' }}
        >
          {orgName || t('navigation.organisation')}
        </Link>
      );
    },
    [t]
  );

  const findMappedReturnByConnection = React.useCallback(
    (connectionId?: string) => {
      if (!connectionId) return undefined;
      return mappedReturns?.find((mr) => mr.connectionId === connectionId);
    },
    [mappedReturns]
  );

  const getReturnTypeLabel = React.useCallback(
    (returnTypeId?: string, fallbackName?: string) => {
      if (returnTypeId) {
        const selected = returnData?.returnTypesEdit?.find(
          (rt) => rt.id === returnTypeId
        );
        if (selected) {
          const label = selected.countryName
            ? `${selected.countryName} - ${selected.name}`
            : selected.name;
          return localizeReturnMappingLabel(
            label,
            countries,
            i18n.language,
            t
          );
        }
      }
      return fallbackName
        ? localizeReturnMappingLabel(
            fallbackName,
            countries,
            i18n.language,
            t
          )
        : '';
    },
    [returnData, countries, i18n.language, t]
  );

  const resolveSubmissionHeaderData = React.useCallback(
    (route: string, search: string) => {
      const { fromGroup, groupId } = parseGroupReturnContextFromSearch(search);
      const { connectionId: urlConnId, mappingId: urlMapId } =
        parseVatReportRoute(route);
      const effectiveMappingId = vatRunData?.mappingId ?? urlMapId;
      const effectiveConnId = vatRunData?.tokenId ?? urlConnId;

      const mappedReturnByContext =
        (effectiveMappingId
          ? mappedReturns?.find((mr) => mr.mappingId === effectiveMappingId)
          : undefined) ||
        (effectiveConnId && effectiveMappingId
          ? mappedReturns?.find(
              (mr) =>
                mr.connectionId === effectiveConnId &&
                mr.mappingId === effectiveMappingId
            )
          : undefined) ||
        findMappedReturnByConnection(effectiveConnId);

      const connectionId =
        mappedReturnByContext?.connectionId ?? effectiveConnId;
      const mappedReturnByConnection =
        findMappedReturnByConnection(connectionId);
      const orgFromStore = connectionId
        ? orgs?.find((o) => o.connectionId === connectionId)
        : undefined;

      const orgName =
        (fromGroup && groupId
          ? orgs?.find((o) => o.uuid === groupId)?.friendlyName
          : undefined) ||
        mappedReturnByConnection?.organisation ||
        orgFromStore?.friendlyName ||
        orgFromStore?.legalName ||
        vatRunData?.connectionName;
      const orgId =
        (fromGroup && groupId ? groupId : undefined) ||
        mappedReturnByConnection?.organisationId ||
        orgFromStore?.uuid;

      const returnTypeId = mappedReturnByContext?.returnTypeId;
      const mappingName =
        getReturnTypeLabel(returnTypeId, mappedReturnByContext?.mappingName) ||
        vatRunData?.taxReportTypeName ||
        '';

      const submissionName =
        vatRunData?.fromDate && vatRunData?.toDate
          ? formatDisplayDateRange(
              vatRunData.fromDate,
              vatRunData.toDate,
              'DD/MM/YYYY',
              i18n.language
            )
          : localizeDigitsForLocale(
              vatRunData?.generationPeriod || '',
              i18n.language
            );

      return {
        orgName,
        orgId,
        mappingName,
        submissionName,
      };
    },
    [
      vatRunData,
      mappedReturns,
      findMappedReturnByConnection,
      orgs,
      getReturnTypeLabel,
      i18n.language,
    ]
  );

  const resolveEntityDetailHeader = React.useCallback(
    (pageName: string, route: string): HeaderResolution | undefined => {
      if (!(pageName === Pages.ENTITIES && route.includes('organisation'))) {
        return undefined;
      }
      const orgIdIndex = routes.organisations.organisation.indexOf(':id');
      const orgId = route.substring(orgIdIndex);
      const orgName =
        mappedReturns?.find((mr) => mr.organisationId === orgId)
          ?.organisation || orgs?.find((o) => o.uuid === orgId)?.friendlyName;

      if (!orgName) return undefined;
      const entitiesListPath = pwaMobileNav
        ? routes.main
        : routes.organisations.all;
      const entitiesListLabel = pwaMobileNav
        ? t('navigation.home')
        : translatePageName(t, pageName);
      return {
        pageTitle: {
          pre: (
            <>
              <Link to={entitiesListPath} style={{ color: 'inherit' }}>
                {entitiesListLabel}
              </Link>
              {' / '}
            </>
          ),
          main: orgName,
        },
        backLink: entitiesListPath,
      };
    },
    [mappedReturns, orgs, t, pwaMobileNav]
  );

  const resolveReturnHeader = React.useCallback(
    (route: string, search: string): HeaderResolution => {
      const orgIdIndex = routes.returns.edit.indexOf(':connectionId');
      const { fromGroup, groupId } = parseGroupReturnContextFromSearch(search);
      const mappingMarker = route.includes('/tifdMapping')
        ? '/tifdMapping'
        : route.includes('/editMapping')
          ? '/editMapping'
          : '';
      let conId = route.substring(orgIdIndex);
      if (mappingMarker) {
        const orgIdEnd = route.indexOf(mappingMarker);
        conId = route.substring(orgIdIndex, orgIdEnd);
        const mapId = route.substring(orgIdEnd + mappingMarker.length + 1);
        if (mapId) {
          const mapRet = mappedReturns?.find((mr) => mr.connectionId === conId);
          const entityOrg = orgs?.find((o) => o.connectionId === conId);
          const groupOrg =
            fromGroup && groupId
              ? orgs?.find((o) => o.uuid === groupId)
              : undefined;
          const orgName = groupOrg
            ? `${groupOrg.friendlyName} (${
                entityOrg?.friendlyName ||
                mapRet?.organisation ||
                t('common.entity')
              })`
            : mapRet?.organisation;
          const orgId = groupOrg?.uuid || mapRet?.organisationId;
          const typeId = mappedReturns?.find(
            (mr) => mr.mappingId === mapId
          )?.returnTypeId;
          const mappingName = getReturnTypeLabel(typeId);

          return {
            pageTitle: {
              pre: (
                <>
                  <Link
                    to={routes.organisations.all}
                    style={{ color: 'inherit' }}
                  >
                    {translatePageName(t, Pages.ENTITIES)}
                  </Link>
                  {' / '}
                  {createOrgBreadcrumb(orgName, orgId)}
                  {' / '}
                </>
              ),
              main: mappingName,
            },
            backLink: orgId
              ? routes.organisations.organisation.replace(':id', orgId)
              : undefined,
          };
        }
      }

      const org = orgs?.find((o) => o.connectionId === conId);
      const groupOrg =
        fromGroup && groupId
          ? orgs?.find((o) => o.uuid === groupId)
          : undefined;
      const orgName = groupOrg
        ? `${groupOrg.friendlyName} (${org?.friendlyName || t('common.entity')})`
        : org?.friendlyName;
      const orgId = groupOrg?.uuid || org?.uuid;
      const mappingName = getReturnTypeLabel(mappingData?.typeId);
      return {
        pageTitle: {
          pre: (
            <>
              <Link to={routes.organisations.all} style={{ color: 'inherit' }}>
                {translatePageName(t, Pages.ENTITIES)}
              </Link>
              {' / '}
              {createOrgBreadcrumb(orgName, orgId)}
              {' / '}
            </>
          ),
          main: mappingName,
        },
        backLink: orgId
          ? routes.organisations.organisation.replace(':id', orgId)
          : undefined,
      };
    },
    [mappedReturns, getReturnTypeLabel, createOrgBreadcrumb, orgs, mappingData, t]
  );

  const resolveUserProfileHeader = React.useCallback(
    (route: string): HeaderResolution | undefined => {
      if (!route.includes('profile/')) return undefined;
      const accountIdIndex = routes.account.details.indexOf(':accountId');
      const accountId = route.substring(accountIdIndex);
      const accountObj = members?.find((mr) => mr.accountUuid === accountId);
      if (!accountObj) return undefined;
      return {
        pageTitle: {
          pre: (
            <>
              <Link to={routes.users} style={{ color: 'inherit' }}>
                {translatePageName(t, Pages.USERS)}
              </Link>
              {' / '}
            </>
          ),
          main: `${accountObj.firstName} ${accountObj.lastName}`,
        },
        backLink: routes.users,
      };
    },
    [members, t]
  );

  const resolveViesCustomersHeader = React.useCallback(
    (pageName: string, route: string): HeaderResolution | undefined => {
      if (
        !(
          pageName === Pages.VIES && route.includes('/customers-and-suppliers/')
        )
      ) {
        return undefined;
      }
      const idx = routes.vies.customersAndSuppliers.indexOf(':connectionId');
      const conId = route.substring(idx);
      const organisation = orgs?.find((o) => o.connectionId === conId);
      return {
        pageTitle: {
          pre: (
            <>
              <Link
                to={routes.vies.qualifiedEntities}
                style={{ color: 'inherit' }}
              >
                {translatePageName(t, Pages.VIES)}
              </Link>
              {' / '}
              {createOrgBreadcrumb(
                organisation?.friendlyName,
                organisation?.uuid
              )}
              {' / '}
            </>
          ),
          main: t('navigation.customers-and-suppliers'),
        },
        backLink: routes.vies.qualifiedEntities,
      };
    },
    [orgs, createOrgBreadcrumb, t]
  );

  const resolveViesContactHeader = React.useCallback(
    (pageName: string, route: string): HeaderResolution | undefined => {
      if (
        !(
          pageName === Pages.VIES &&
          route.includes('/vat-checker/contact-details/') &&
          contacts &&
          orgs
        )
      ) {
        return undefined;
      }

      const idx = routes.vies.contact.indexOf(':connectionId');
      const endIdx = route.indexOf('/contact/');
      const conId = route.substring(idx, endIdx);
      const organisation = orgs?.find((o) => o.connectionId === conId);
      const contId = route.lastIndexOf('/');
      const contactId = route.substring(contId + 1);
      const contactName: any = contacts?.find(
        (o) => o?.contactUuid === contactId
      )?.name;
      return {
        pageTitle: {
          pre: (
            <>
              <Link
                to={routes.vies.qualifiedEntities}
                style={{ color: 'inherit' }}
              >
                {translatePageName(t, Pages.VIES)}
              </Link>
              {' / '}
              {createOrgBreadcrumb(
                organisation?.friendlyName,
                organisation?.uuid
              )}
              {' / '}
              <Link
                to={routes.vies.customersAndSuppliers.replace(
                  ':connectionId',
                  conId
                )}
                style={{ color: 'inherit' }}
              >
                {t('navigation.customers-and-suppliers')}
              </Link>
              {' / '}
            </>
          ),
          main: contactName,
        },
        backLink: conId
          ? routes.vies.customersAndSuppliers.replace(':connectionId', conId)
          : undefined,
      };
    },
    [contacts, orgs, createOrgBreadcrumb, t]
  );

  React.useEffect(() => {
    const route = currentPage.route;
    const pageName = currentPage.name;
    const entityHeader = resolveEntityDetailHeader(pageName, route);
    if (entityHeader) {
      setPageTitle(entityHeader.pageTitle);
      setBackLink(entityHeader.backLink ?? routes.main);
      setShowLine(false);
      return;
    }

    if (pageName === Pages.RETURN) {
      const returnHeader = resolveReturnHeader(route, location.search);
      setPageTitle(returnHeader.pageTitle);
      returnHeader.backLink && setBackLink(returnHeader.backLink);
      setShowLine(true);
      return;
    }

    const userHeader = resolveUserProfileHeader(route);
    if (userHeader) {
      setPageTitle(userHeader.pageTitle);
      userHeader.backLink && setBackLink(userHeader.backLink);
      setShowLine(false);
      return;
    }

    // SUBMISSION page (VAT return report)
    if (pageName === Pages.SUBMISSION) {
      const { orgName, orgId, mappingName, submissionName } =
        resolveSubmissionHeaderData(route, location.search);

      if (pwaMobileNav) {
        const entityPath = orgId
          ? routes.organisations.organisation.replace(':id', orgId)
          : routes.main;
        setPageTitle({
          pre: (
            <>
              <Link to={routes.main} style={{ color: 'inherit' }}>
                {t('navigation.home')}
              </Link>
              {' / '}
              {orgId ? (
                <Link to={entityPath} style={{ color: 'inherit' }}>
                  {orgName || t('navigation.organisation')}
                </Link>
              ) : (
                <span style={{ color: 'inherit' }}>
                  {orgName || t('navigation.organisation')}
                </span>
              )}
              {' / '}
            </>
          ),
          main: mappingName || submissionName || t('navigation.submission'),
        });
        setBackLink(
          orgId ? buildPwaEntityTaxReturnsNavigateUrl(orgId) : routes.main
        );
        setShowLine(false);
        return;
      }

      setPageTitle({
        pre: (
          <>
            <Link to={routes.organisations.all} style={{ color: 'inherit' }}>
              {translatePageName(t, Pages.ENTITIES)}
            </Link>
            {' / '}
            {createOrgBreadcrumb(orgName, orgId)}
            {' / '}
            {mappingName || t('navigation.submission')}
            {' / '}
          </>
        ),
        main: submissionName,
      });
      orgId &&
        setBackLink(routes.organisations.organisation.replace(':id', orgId));
      return;
    }

    // VIES - customers and suppliers
    const viesCustomersHeader = resolveViesCustomersHeader(pageName, route);
    if (viesCustomersHeader) {
      setPageTitle(viesCustomersHeader.pageTitle);
      viesCustomersHeader.backLink && setBackLink(viesCustomersHeader.backLink);
      return;
    }

    const viesContactHeader = resolveViesContactHeader(pageName, route);
    if (viesContactHeader) {
      setPageTitle(viesContactHeader.pageTitle);
      viesContactHeader.backLink && setBackLink(viesContactHeader.backLink);
      return;
    }

    // PRICING - checkout
    if (pageName === Pages.PRICING && route.includes('/checkout')) {
      setPageTitle({
        pre: translatePageName(t, Pages.PRICING) + ' / ',
        main: t('navigation.checkout'),
      });
      setBackLink(routes.pricing.root);
      return;
    }

    // Default
    setPageTitle({ main: translatePageName(t, pageName), pre: null });
    setShowLine(false);
  }, [
    currentPage,
    orgs,
    mappedReturns,
    members,
    vatRunData,
    contacts,
    returnData,
    mappingData,
    createOrgBreadcrumb,
    getReturnTypeLabel,
    resolveSubmissionHeaderData,
    resolveEntityDetailHeader,
    resolveReturnHeader,
    location.search,
    resolveUserProfileHeader,
    resolveViesCustomersHeader,
    resolveViesContactHeader,
    pwaMobileNav,
    t,
  ]);

  const handlePwaBackClick = React.useCallback(() => {
    clearOrganisationDetailsTabStorage();
    const target = backLink || routes.main;
    const leavingEntityForHome =
      location.pathname.includes('/entities/organisation/') &&
      (target === routes.main || target === routes.organisations.all);
    if (leavingEntityForHome) {
      markSkipPwaHomeRestore();
      lockLeaveEntityNavigation();
      navigate(routes.main, { state: { pwaExplicitHome: true } });
      return;
    }
    navigate(target);
  }, [backLink, navigate, location.pathname]);

  const getHeaderWidthClass = React.useMemo(() => {
    const { name, route } = currentPage;

    const centerPages = [
      Pages.DASHBOARD,
      Pages.USERS,
      Pages.BILLING,
      Pages.RETURNS,
    ];

    const isViesCustomersSuppliers =
      name === Pages.VIES && route.includes('customers-and-suppliers');
    const isEntitiesList =
      name === Pages.ENTITIES && !route.includes('organisation');
    const isViesList =
      name === Pages.VIES && !route.includes('contact-details');

    if (
      centerPages.includes(name) ||
      isViesCustomersSuppliers ||
      isEntitiesList ||
      isViesList
    ) {
      return 'header-container-center';
    }

    return '';
  }, [currentPage]);
  const headerClass =
    menuSize === MenuSize.WIDE ? 'header-narrow' : 'header-wide';
  const hasPreTitle =
    !!pageTitle?.pre &&
    (typeof pageTitle.pre !== 'string' || !pageTitle.pre.includes('undefined'));
  const hasMainTitle =
    !!pageTitle?.main && !pageTitle.main.includes('undefined');
  const isPwaCompactHeader = pwaMobileNav && !hasPreTitle;
  /** Home / Profile / Accessibility / entity detail: no sticky logo bar (mobile PWA).
   * Entity pages render `PwaEntityBar` in-page instead. */
  const isPwaEntityOrganisationPage =
    pwaMobileNav && location.pathname.includes('/entities/organisation/');
  const isPwaBareHeader =
    pwaMobileNav &&
    (currentPage.name === Pages.DASHBOARD ||
      currentPage.name === Pages.PROFILE ||
      currentPage.name === Pages.ACCESSIBILITY ||
      currentPage.name === Pages.SUBMISSION ||
      isPwaEntityOrganisationPage);
  /** Brand logo on phone/PWA pages that keep the sticky header. */
  const isPwaBrandHeader = pwaMobileNav && !isPwaBareHeader;
  const isPwaSubmissionPage =
    pwaMobileNav && currentPage.name === Pages.SUBMISSION;

  const headerRef = React.useRef<HTMLDivElement>(null);
  const pwaHeaderLayoutKey = [
    pageTitle?.pre,
    pageTitle?.main,
    isPwaCompactHeader,
    isPwaBrandHeader,
    isPwaBareHeader,
    isPwaSubmissionPage,
    showLine,
  ].join('|');
  useSyncPwaHeaderHeight(
    headerRef,
    pwaMobileNav && !isPwaBareHeader && !isPwaSubmissionPage,
    pwaHeaderLayoutKey
  );

  return (
    <div
      ref={headerRef}
      className={`header-container ${headerClass}${pwaMobileNav ? ' header-container--pwa' : ''}${isPwaCompactHeader ? ' header-container--pwa-compact' : ''}${isPwaBrandHeader ? ' header-container--pwa-home' : ''}${isPwaBareHeader ? ' header-container--pwa-home-bare' : ''}${isPwaSubmissionPage ? ' header-container--pwa-submission' : ''}`}
      style={
        showLine && !isPwaBareHeader
          ? { borderBottom: '1px solid var(--color-border, #dbeae3)' }
          : {}
      }
    >
      {pwaMobileNav ? (
        isPwaBareHeader ? null : (
        <div
          className={`scope-pwa-header${isPwaCompactHeader ? ' scope-pwa-header--compact' : ''}${isPwaBrandHeader ? ' scope-pwa-header--home' : ''}`}
        >
          <div className="scope-pwa-header__actions">
            <img
              className="scope-pwa-header__brand-logo"
              src={LogoNormal}
              alt={process.env.PROJECT_NAME || 'generate.TAX'}
            />
          </div>
          {!isPwaCompactHeader && (hasPreTitle || hasMainTitle) && (
            <div className="scope-pwa-header__title-row">
              {hasPreTitle && (
                <button
                  type="button"
                  className="scope-pwa-header__back"
                  aria-label={t('common.back')}
                  onClick={handlePwaBackClick}
                >
                  <IconBack style={{ width: 13, height: 13 }} aria-hidden />
                </button>
              )}
              <Typography.Title level={4} className="scope-pwa-header__title">
                {hasPreTitle && (
                  <span className="scope-pwa-header__pre">
                    {pageTitle.pre}
                  </span>
                )}
                {hasMainTitle && (
                  <span className="scope-pwa-header__main">{pageTitle?.main}</span>
                )}
              </Typography.Title>
            </div>
          )}
        </div>
        )
      ) : (
      <div className={getHeaderWidthClass}>
        <Row
          justify={'space-between'}
          align={'middle'}
          style={{ marginTop: '15px' }}
        >
          <Col>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {hasPreTitle && (
                <>
                  <Link
                    to={backLink}
                    style={{ marginInlineEnd: 15 }}
                    aria-label={
                      typeof pageTitle.pre === 'string'
                        ? `${t('common.back')}: ${pageTitle.pre}`
                        : t('common.back')
                    }
                    onClick={clearOrganisationDetailsTabStorage}
                  >
                    <IconBack style={{ width: 13, height: 13 }} aria-hidden />
                  </Link>
                  <span
                    style={{ fontWeight: 400, color: 'var(--color-text-muted, #5a6a6a)', fontSize: 16 }}
                  >
                    {pageTitle.pre}
                  </span>
                </>
              )}
              {hasMainTitle && pageTitle?.main}
            </Typography.Title>
          </Col>
          <Col flex="none">
            <Space size={'large'}>
              <div style={{ marginTop: -7 }}>
                <SearchWrapper ref={searchRef}>
                  <StyledSearchOutlined />
                  <RoundAutoComplete
                    options={allOptions}
                    onChange={handleChange}
                    onSelect={onSelect}
                    placeholder={t('common.search')}
                    aria-label={t('common.search')}
                    value={searchValue}
                    allowClear
                    filterOption={false}
                    popupRender={renderDropdown}
                    style={{ width: '250px' }}
                  />
                </SearchWrapper>
              </div>
              {(() => {
                const canAddEntity =
                  loggedUser?.canManageOrgs &&
                  userLicence?.licenseType !== LicenseType.BLOCKED &&
                  (userLicence?.licenseDaysLeft ?? 0) > 0;
                let disabledReason: string | null = null;
                if (!canAddEntity) {
                  if (!loggedUser?.canManageOrgs) {
                    disabledReason = t(
                      'global.new-entity-disabled-no-permission'
                    );
                  } else if (userLicence?.licenseType === LicenseType.BLOCKED) {
                    disabledReason = t(
                      'global.new-entity-disabled-license-blocked'
                    );
                  } else {
                    disabledReason = t(
                      'global.new-entity-disabled-license-expired'
                    );
                  }
                }
                const button = (
                  <FeatureDotBadge dotId="entities-new-entity-button">
                    <ScopeButton
                      type={'primary'}
                      onClick={onAddEntity}
                      width={'auto'}
                      height={'40px'}
                      disabled={!canAddEntity}
                      style={
                        !canAddEntity
                          ? {
                              pointerEvents: 'none' as const,
                              minWidth: 135,
                              paddingInline: 16,
                            }
                          : { minWidth: 135, paddingInline: 16 }
                      }
                    >
                      <Space>
                        <PlusOutlined />
                        {t('navigation.new-entity')}
                      </Space>
                    </ScopeButton>
                  </FeatureDotBadge>
                );
                return disabledReason ? (
                  <StyledTooltip title={disabledReason} placement="bottom">
                    <span style={{ display: 'inline-block' }}>{button}</span>
                  </StyledTooltip>
                ) : (
                  button
                );
              })()}
            </Space>
          </Col>
        </Row>
      </div>
      )}
    </div>
  );
};
